import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "../lib/api";
import { ArticleCard } from "../components/ArticleCard";
import { TrendingSidebar } from "../components/TrendingSidebar";
import { CATEGORY_LABELS } from "../lib/constants";

// Section heads are solid lime bars: black slab type inside the block, with the
// "view all" control butted onto the end as a black box.
function SectionHeader({ title, to }) {
    return (
        <div className="flex items-stretch border-[3px] border-foreground bg-accent shadow-card mb-7">
            <h2 className="font-display text-lg sm:text-2xl uppercase tracking-[-0.01em] leading-none text-accent-foreground px-4 py-3.5 flex items-center">
                {title}
            </h2>
            {to && (
                <Link
                    to={to}
                    className="group ml-auto shrink-0 flex items-center gap-2 border-l-[3px] border-foreground bg-surface px-4 eyebrow text-foreground hover:bg-foreground hover:text-accent transition-colors duration-150"
                >
                    View all
                    <ArrowRight size={13} strokeWidth={3} className="transition-transform duration-150 group-hover:translate-x-1" />
                </Link>
            )}
        </div>
    );
}

function HomeSkeleton() {
    return (
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <p className="inline-flex items-center gap-2.5 border-2 border-foreground bg-accent px-3 py-2 eyebrow text-accent-foreground mb-8">
                <span className="w-2 h-2 bg-accent-foreground animate-pulse-dot" />
                Loading the newsroom…
            </p>
            <div className="border-[3px] border-foreground shadow-card">
                <div className="grid grid-cols-1 lg:grid-cols-12">
                    <div className="lg:col-span-7 p-8 space-y-4">
                        <div className="shimmer h-6 w-28" />
                        <div className="shimmer h-14 w-full" />
                        <div className="shimmer h-14 w-4/5" />
                        <div className="shimmer h-4 w-2/3" />
                    </div>
                    <div className="lg:col-span-5 shimmer aspect-[16/10] lg:aspect-auto border-t-[3px] lg:border-t-0 lg:border-l-[3px] border-foreground" />
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
                {[0, 1, 2].map((i) => (
                    <div key={i} className="border-2 border-foreground p-3 flex gap-4">
                        <div className="flex-1 space-y-2">
                            <div className="shimmer h-4 w-16" />
                            <div className="shimmer h-4 w-full" />
                            <div className="shimmer h-4 w-2/3" />
                        </div>
                        <div className="shimmer w-[88px] h-[72px] shrink-0" />
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function Home() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/home").then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
    }, []);

    if (loading) {
        return <HomeSkeleton />;
    }

    if (!data || !data.featured) {
        return (
            <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-24" data-testid="home-empty">
                <div className="panel max-w-lg mx-auto p-12">
                    <span className="eyebrow inline-block border-2 border-foreground bg-accent px-2 py-1 text-accent-foreground">Standby</span>
                    <h2 className="font-display text-3xl uppercase leading-[0.95] tracking-[-0.02em] mt-5">Fetching the latest stories</h2>
                    <p className="mt-4 text-sm text-dim leading-relaxed">
                        Articles are being pulled from The Guardian. Please check back in a moment.
                    </p>
                </div>
            </div>
        );
    }

    const { featured, latest, trending, sections } = data;

    return (
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="home-page">
            {/* Lead story runs the full measure */}
            <ArticleCard article={featured} variant="hero" />

            {/* Latest — a row of bordered boxes */}
            <div className="mt-16">
                <SectionHeader title="Latest News" />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {latest.slice(0, 6).map((a) => (
                        <ArticleCard key={a.id} article={a} variant="compact" />
                    ))}
                </div>
            </div>

            {/* Category sections + trending sidebar */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 mt-20">
                <div className="lg:col-span-8 space-y-16">
                    {Object.entries(sections).map(([cat, arts]) => (
                        <section key={cat} data-testid={`home-section-${cat}`}>
                            <SectionHeader title={CATEGORY_LABELS[cat] || cat} to={`/category/${cat}`} />
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
                                {arts.map((a) => (
                                    <ArticleCard key={a.id} article={a} />
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
                <div className="lg:col-span-4">
                    <TrendingSidebar items={trending} />
                </div>
            </div>
        </div>
    );
}
